import { internalLogger } from '../../interface/v1/clientOsLogger';
import bindAllMethods from '../../utils/bindAllMethods';
import { Refreshable } from '../../utils/refreshable/IRefreshable';

// Channel name for browser-composer communication
const CHANNEL_NAME = 'jarvis-navigation-shell-status';

export default class RefreshNavMfe extends Refreshable {
  private _webUIRefreshOnAuthChange: boolean;
  private _channel: BroadcastChannel | null;

  constructor({
    webUIRefreshOnAuthChange
  }: {
    webUIRefreshOnAuthChange: boolean;
  }) {
    super();
    bindAllMethods(this);
    this._webUIRefreshOnAuthChange = webUIRefreshOnAuthChange;

    // BroadcastChannel is absent on Safari < 15.4 (e.g. iOS 12.5.8).
    // 'in globalThis' is the idiomatic feature-detection guard: safe, explicit,
    // and avoids the anti-pattern of using exceptions for control flow.
    if ('BroadcastChannel' in globalThis) {
      this._channel = new BroadcastChannel(CHANNEL_NAME);
    } else {
      this._channel = null;
      internalLogger?.warn?.(
        'RefreshNavMfe: BroadcastChannel is not supported in this environment. Nav MFE refresh will be disabled.'
      );
    }

    this.setRefreshMethods({
      init: this.refresh.bind(this),
      refresh: this.refresh.bind(this)
    });
  }

  public async refresh(): Promise<void> {
    if (!this._webUIRefreshOnAuthChange) {
      /* istanbul ignore next */
      internalLogger?.debug?.d4({
        className: 'RefreshNavMfe',
        methodName: 'refresh',
        message: 'UI refresh on auth change is disabled, skipping refresh.',
        variables: { webUIRefreshOnAuthChange: this._webUIRefreshOnAuthChange }
      });
      return;
    }
    /* istanbul ignore next */
    internalLogger?.debug?.d2({
      className: 'RefreshNavMfe',
      methodName: 'refresh',
      message: 'Sending message to refresh the NAV MfE.',
      parameters: {}
    });

    if (!this._channel) {
      internalLogger?.warn?.(
        'RefreshNavMfe: BroadcastChannel is not available. Skipping NAV MFE refresh.'
      );
      return;
    }

    // Sending initial message to disable the NAV MfE.
    this._channel.postMessage({
      message: 'Disabling react-nav MFE.',
      type: 'disable'
    });

    // Now we can send a message to enable the NAV MfE after a delay
    // This simulates a condition where the MFE needs to be re-initialized
    // after some time.
    setTimeout(() => {
      this._channel?.postMessage({
        message: 'Reloading after 1 second.',
        type: 'enable'
      });
    }, 1000);
  }
}
